import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Buildings, FileText, PenNib, MapTrifold, MapPin, Path, ArrowRight,
  Info, UploadSimple, Plus, Trash, CheckCircle, LockSimple, Files as FilesIcon,
  ArrowsClockwise, DownloadSimple, CircleNotch, MagnifyingGlass,
} from "@phosphor-icons/react";

const CATS = [
  { id: "port", icon: ArrowsClockwise, title: "Port existing LISP → this target",
    desc: "Reuse the commands & actions from this client's uploaded .lsp, rebuilt for the target platform using their template layers/styles.",
    starter: "Reuse the command names and actions from this client's uploaded LISP files. Recreate each command for the target platform, keeping the EXACT same command names, prompts and workflow, but use this client's finished-CAD template layers, text styles, dim styles and blocks instead of the layer/style names hardcoded in the original LISP." },
  { id: "landdev", icon: MapTrifold, title: "Survey / Land Dev task",
    desc: "Contours, grading, section sheets, MP→TP bearing basis, pay items.",
    starter: "Create a Civil 3D AutoLISP command that " },
  { id: "lot", icon: Path, title: "Lot boundary / legal",
    desc: "Draw a parcel from a deed/legal description or a bearing sequence; report closure & area.",
    starter: "Create an AutoLISP command that draws a closed lot boundary from pasted bearing/distance courses, reports closure and area, on the boundary layer." },
  { id: "points", icon: MapPin, title: "Points / field-to-finish",
    desc: "Import survey points from CSV/TXT, place on layers, connect linework by field code.",
    starter: "Create an AutoLISP command that imports survey points from a CSV (P,N,E,Z,Desc), inserts a point block on the correct layer per description code, and labels the point number and elevation." },
  { id: "draw", icon: PenNib, title: "Draw / annotate something",
    desc: "Any drafting automation — draw entities, label, batch-edit, insert blocks.",
    starter: "Create an AutoLISP command that " },
  { id: "free", icon: FileText, title: "Free-form prompt",
    desc: "Type your own request in plain English. The AI grounds it in this client's standards.",
    starter: "" },
];

const StepHead = ({ n, title, children }) => (
  <div className="flex gap-3">
    <div className="shrink-0 h-6 w-6 rounded-full bg-[var(--primary)] text-white text-[12px] font-mono flex items-center justify-center">{n}</div>
    <div className="min-w-0">
      <div className="text-[13px] font-mono text-[var(--text)]">{title}</div>
      {children && <div className="text-[12px] text-[var(--text-dim)] leading-snug mt-0.5">{children}</div>}
    </div>
  </div>
);

export const GeneratorGuide = ({
  clients = [], clientId, onSelectClient, onCreateClient,
  fileCount = 0, files = [], onAddInput, onAddOutput, onDeleteFile,
  onStarter, tools = [], onTool, children,
  platform = "AutoCAD Civil 3D", version = "2018", targetLabel = "Civil 3D 2018",
}) => {
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const activeClient = clients.find((c) => c.id === clientId);

  const [lispInfo, setLispInfo] = useState(null);
  const [porting, setPorting] = useState(false);
  const [portJob, setPortJob] = useState(null);
  const [portStage, setPortStage] = useState("");
  const [portDone, setPortDone] = useState(null);
  const [cmdQuery, setCmdQuery] = useState("");

  useEffect(() => {
    if (!clientId) { setLispInfo(null); return; }
    api.lispCommands(clientId).then(setLispInfo).catch(() => setLispInfo(null));
  }, [clientId, files.length]);

  const runPort = async () => {
    if (!clientId || porting) return;
    setPorting(true); setPortDone(null); setPortStage("Starting…");
    try {
      const { job_id } = await api.portLisp(clientId, { platform, version });
      setPortJob(job_id);
      const started = Date.now();
      while (Date.now() - started < 900000) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await api.portLispStatus(job_id);
        if (s.progress?.stage) setPortStage(s.progress.stage);
        if (s.status === "done") { setPortDone(s.result); toast.success("Ported all routines to " + targetLabel); break; }
        if (s.status === "error") { toast.error(s.error || "Port failed"); break; }
        if (s.status === "cancelled") { toast.message("Port cancelled"); break; }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Port failed"); }
    finally { setPorting(false); }
  };

  const downloadPort = async () => {
    if (!portJob) return;
    try {
      const blob = await api.portLispZip(portJob);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "ported_lisp.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const onOnePager = async () => {
    if (!clientId) return;
    const t = toast.loading("Building command one-pager…");
    try {
      const blob = await api.onePagerPdf(clientId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "command_one_pager.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("One-pager ready", { id: t });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build PDF", { id: t }); }
  };

  const create = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try { await onCreateClient(name); setNewName(""); }
    finally { setCreating(false); }
  };

  return (
    <div className="absolute inset-0 overflow-y-auto" data-testid="generator-guide">
      <div className="max-w-3xl mx-auto px-6 py-8 flex flex-col gap-5">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--primary)]">Start here</div>
          <h1 className="text-2xl font-mono text-[var(--text)] mt-1">Everything for one generation — in one place</h1>
          <p className="text-[13px] text-[var(--text-dim)] mt-1">Pick or create the client, add their reference &amp; finished CAD, describe the routine, set the options and Generate. It's all saved under that client.</p>
        </div>

        {/* Step 1 — client */}
        <div className="border border-[var(--border)] rounded-[5px] p-4 flex flex-col gap-3" data-testid="guide-step-client">
          <StepHead n={1} title="Who is this for? Pick or create a client">
            The AI grounds every routine in this client's layers, styles and existing commands.
          </StepHead>
          <div className="pl-9 flex flex-col gap-3">
            {clients.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="guide-client-list">
                {clients.map((c) => {
                  const active = c.id === clientId;
                  return (
                    <button key={c.id} onClick={() => onSelectClient(c.id)} data-testid={`guide-client-${c.id}`}
                      className={`flex items-center gap-1.5 h-8 px-3 rounded-[3px] border text-[12px] font-mono transition-colors ${active ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-[var(--border)] bg-[var(--bg)] text-[var(--text-dim)] hover:text-[var(--text)] hover:border-[var(--primary)]/50"}`}>
                      <Buildings size={13} weight="bold" />
                      {c.name}
                      <span className="opacity-60">· {c.file_count}f/{c.gen_count}g</span>
                      {active && <CheckCircle size={13} weight="fill" className="text-[var(--primary)]" />}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") create(); }}
                placeholder="New client name (e.g. Acme Surveying)" data-testid="guide-new-client-input"
                className="flex-1 h-9 px-3 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]" />
              <button onClick={create} disabled={creating || !newName.trim()} data-testid="guide-create-client"
                className="h-9 px-3.5 rounded-[3px] bg-[var(--primary)] hover:bg-[var(--primary-hover)] disabled:opacity-40 text-white text-[13px] font-mono flex items-center gap-1.5">
                <Plus size={14} weight="bold" /> Create
              </button>
            </div>
            {activeClient ? (
              <div className="flex items-center gap-2 text-[12px] font-mono text-[var(--text)]" data-testid="guide-active-client">
                <Buildings size={14} weight="bold" className="text-[var(--primary)]" /> Working on <b>{activeClient.name}</b>
                <span className="text-[var(--text-dim)]">·</span>
                <UploadSimple size={13} /> {fileCount} document{fileCount === 1 ? "" : "s"} loaded
              </div>
            ) : (
              <div className="flex items-center gap-2 text-[12px] font-mono text-amber-500" data-testid="guide-no-client">
                <Info size={14} weight="bold" /> No client selected — uploads &amp; generation need an active client.
              </div>
            )}
          </div>
        </div>

        {/* Step 2 — documents */}
        <div className="border border-[var(--border)] rounded-[5px] p-4 flex flex-col gap-3" data-testid="guide-step-docs">
          <StepHead n={2} title="Add this client's documentation (input & output)">
            Upload reference material (existing LISP, deeds, CSV points) and their finished / output CAD so generated code matches their exact standards.
          </StepHead>
          <div className="pl-9 flex flex-col gap-3">
            {clientId && (
              <div className="flex items-center justify-end -mt-1">
                <button onClick={onOnePager} data-testid="one-pager-btn"
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-[3px] border border-[var(--border)] text-[11px] font-mono text-[var(--text-dim)] hover:text-[var(--primary)] hover:border-[var(--primary)] transition-colors">
                  <FileText size={13} weight="bold" /> Command one-pager (PDF)
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button onClick={onAddInput} disabled={!clientId} data-testid="guide-add-input"
                className="flex flex-col items-start gap-1 text-left border border-dashed border-[var(--border)] rounded-[4px] p-3 hover:border-[var(--primary)] hover:bg-[var(--primary)]/5 disabled:opacity-40 disabled:hover:border-[var(--border)] transition-colors">
                <span className="flex items-center gap-2 text-[13px] font-mono text-[var(--text)]"><UploadSimple size={15} weight="bold" className="text-[var(--primary)]" /> Input reference docs &amp; existing LISP</span>
                <span className="text-[11px] text-[var(--text-dim)] leading-snug">Existing .lsp (e.g. progeCAD), DWG / DXF / CSV / legal — uploaded LISP commands are reused on every generation</span>
              </button>
              <button onClick={onAddOutput} disabled={!clientId} data-testid="guide-add-output"
                className="flex flex-col items-start gap-1 text-left border border-dashed border-[var(--border)] rounded-[4px] p-3 hover:border-[var(--primary)] hover:bg-[var(--primary)]/5 disabled:opacity-40 disabled:hover:border-[var(--border)] transition-colors">
                <span className="flex items-center gap-2 text-[13px] font-mono text-[var(--text)]"><FilesIcon size={15} weight="bold" className="text-[var(--primary)]" /> Finished / output CAD</span>
                <span className="text-[11px] text-[var(--text-dim)] leading-snug">Template / delivered DWG-DXF — matches layers, styles, blocks</span>
              </button>
            </div>
            {!clientId && (
              <div className="text-[11px] text-amber-500 font-mono">Select or create a client above before uploading.</div>
            )}
            {clientId && files.length === 0 && (
              <div className="text-[11px] text-[var(--text-dim)]">No documents yet for this client. Add a finished DWG/DXF so generated code uses their exact layers &amp; styles.</div>
            )}
            {files.length > 0 && (
              <div className="border border-[var(--border)] rounded-[4px] divide-y divide-[var(--border)]/50 max-h-56 overflow-y-auto" data-testid="guide-file-list">
                {files.map((m) => (
                  <div key={m.id} className="group flex items-start justify-between gap-2 px-3 py-2 hover:bg-[var(--surface-2)] transition-colors" data-testid={`guide-file-${m.id}`}>
                    <div className="min-w-0">
                      <div className="text-[13px] truncate flex items-center gap-1.5">
                        {m.builtin && <LockSimple size={11} className="text-[var(--text-dim)] shrink-0" />}
                        {m.name}
                      </div>
                      <div className="text-[10px] text-[var(--text-dim)] font-mono mt-0.5 flex items-center gap-1.5">
                        {m.category === "template" && <span className="text-[var(--primary)]">OUTPUT / TEMPLATE ·</span>}
                        {(m.type || "").toUpperCase()} · {m.num_chunks} sections
                        {(String(m.type || "").toLowerCase() === "lsp" || String(m.name || "").toLowerCase().endsWith(".lsp")) && (
                          <span className="text-[var(--success)]" data-testid={`guide-lisp-reuse-${m.id}`}>· ↻ commands reused</span>
                        )}
                        {m.category === "template" && m.layers?.length > 0 && (
                          <span className="text-[var(--success)]">· ✓ {m.layers.length} layers</span>
                        )}
                      </div>
                    </div>
                    {!m.builtin && (
                      <button onClick={() => onDeleteFile(m.id, m.name)} data-testid={`guide-delete-file-${m.id}`}
                        className="opacity-0 group-hover:opacity-100 text-[var(--text-dim)] hover:text-[var(--alert)] transition-opacity mt-0.5">
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {lispInfo?.total_files > 0 && (
              <div className="border border-[var(--primary)]/30 bg-[var(--primary)]/5 rounded-[4px] p-3 flex flex-col gap-2" data-testid="lisp-port-panel">
                <div className="text-[12px] font-mono text-[var(--text)] flex items-center gap-1.5">
                  <ArrowsClockwise size={14} weight="bold" className="text-[var(--primary)]" />
                  Detected {lispInfo.total_commands} command{lispInfo.total_commands === 1 ? "" : "s"} across {lispInfo.total_files} LISP file{lispInfo.total_files === 1 ? "" : "s"} — reused on every generation.
                </div>
                <div className="flex items-center gap-1.5">
                  <MagnifyingGlass size={13} className="text-[var(--text-dim)]" />
                  <input value={cmdQuery} onChange={(e) => setCmdQuery(e.target.value)} data-testid="guide-cmd-search"
                    placeholder="Search a command (e.g. bndy, spot, contour)…"
                    className="flex-1 h-8 px-2 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[12px] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]" />
                </div>
                <div className="flex flex-col gap-1 max-h-40 overflow-y-auto" data-testid="guide-cmd-list">
                  {lispInfo.files.map((f) => {
                    const q = cmdQuery.trim().toLowerCase();
                    const shown = q ? f.commands.filter((c) => c.toLowerCase().includes(q)) : f.commands;
                    if (q && shown.length === 0) return null;
                    return (
                      <div key={f.id} className="text-[11px] leading-relaxed" data-testid={`lisp-file-cmds-${f.id}`}>
                        <span className="font-mono text-[var(--text-dim)]">{f.name}:</span>{" "}
                        {shown.length
                          ? shown.map((c) => (
                              <button key={c} data-testid={`cmd-chip-${c}`} title={`Regenerate c:${c} for ${targetLabel}`}
                                onClick={() => onStarter(`Recreate the command c:${c} for the target platform — reuse its exact name, prompts, order of operations and behavior from this client's uploaded LISP (${f.name}), but draw on this client's template layers/styles.`)}
                                className="inline-flex items-center font-mono text-[var(--primary)] bg-[var(--primary)]/10 hover:bg-[var(--primary)]/25 rounded-[2px] px-1 mr-1 mb-1 transition-colors cursor-pointer">
                                c:{c}
                              </button>
                            ))
                          : <span className="text-[var(--text-dim)] italic">no c: commands detected</span>}
                      </div>
                    );
                  })}
                  {cmdQuery.trim() && !lispInfo.files.some((f) => f.commands.some((c) => c.toLowerCase().includes(cmdQuery.trim().toLowerCase()))) && (
                    <div className="text-[11px] text-[var(--text-dim)] italic" data-testid="guide-cmd-none">No command matches "{cmdQuery.trim()}".</div>
                  )}
                </div>
                <div className="text-[10px] text-[var(--text-dim)] font-mono">Tip: click any command to drop a "regenerate this routine" prompt into the box below.</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={runPort} disabled={porting} data-testid="batch-port-btn"
                    className="h-8 px-3 rounded-[3px] bg-[var(--primary)] hover:bg-[var(--primary-hover)] disabled:opacity-50 text-white text-[12px] font-mono flex items-center gap-1.5">
                    {porting ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />}
                    Batch port all → {targetLabel}
                  </button>
                  {porting && <span className="text-[11px] font-mono text-[var(--text-dim)]" data-testid="port-stage">{portStage}</span>}
                  {portDone && (
                    <button onClick={downloadPort} data-testid="port-download-btn"
                      className="h-8 px-3 rounded-[3px] border border-[var(--success)]/60 text-[var(--success)] text-[12px] font-mono flex items-center gap-1.5 hover:bg-[var(--success)]/10">
                      <DownloadSimple size={13} weight="bold" /> Download ported package (.zip)
                    </button>
                  )}
                </div>
                {portDone && (
                  <div className="text-[11px] font-mono text-[var(--text-dim)]" data-testid="port-result">
                    Ported {portDone.files?.length} file(s) to {portDone.platform} {portDone.version}. Uses this client's template layers/styles; review before loading.
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Step 3 — describe + options + generate */}
        <div className="border border-[var(--border)] rounded-[5px] p-4 flex flex-col gap-3" data-testid="guide-step-generate">
          <StepHead n={3} title="Describe the routine, set options & Generate">
            Pick a starting point (it fills the box below) or write your own. Then choose target platforms, scale and refine options — right here — and Generate.
          </StepHead>
          <div className="pl-9 flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CATS.map((c) => (
                <button key={c.id} data-testid={`guide-cat-${c.id}`} onClick={() => onStarter(c.starter)}
                  className="text-left border border-[var(--border)] rounded-[4px] p-3 hover:border-[var(--primary)] hover:bg-[var(--primary)]/5 transition-colors group">
                  <div className="flex items-center gap-2">
                    <c.icon size={16} weight="bold" className="text-[var(--primary)]" />
                    <span className="text-[13px] font-mono text-[var(--text)]">{c.title}</span>
                    <ArrowRight size={13} className="ml-auto opacity-0 group-hover:opacity-100 text-[var(--primary)]" />
                  </div>
                  <div className="text-[11px] text-[var(--text-dim)] mt-1 leading-snug">{c.desc}</div>
                </button>
              ))}
            </div>

            {/* Tool presets live inside the compose dock below (TOOLS row) to avoid duplication */}

            {/* The full compose + options + Generate dock lives here now */}
            <div className="border border-[var(--border)] rounded-[4px] bg-[var(--surface-2)]" data-testid="guide-compose">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
